
import React from 'react';
import { motion } from 'framer-motion';
import { X, Clock, FileText, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export function PainPointsSection() {
  return (
    <section className="py-24 bg-slate-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-red-50 rounded-bl-[100px] opacity-70"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-orange-50 rounded-tr-[100px] opacity-70"></div>
      
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            Stop Wasting Hours Manually Analyzing Sleep Data
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Before */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <div className="aspect-w-4 aspect-h-3">
                <img 
                  src="https://gbmarketplacestorage.blob.core.windows.net/sleepnest/studying.svg" 
                  alt="Stressed sleep consultant with messy paperwork" 
                  className="object-cover w-full h-full brightness-90"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-red-900/40 to-transparent"></div>
              </div>
              
              {/* Floating Paper Elements */}
              <div className="absolute top-6 right-6 -rotate-6 bg-white p-3 rounded-lg shadow-lg max-w-[120px]">
                <div className="text-xs border-b border-gray-200 pb-1 mb-1 font-medium">Sleep Log</div>
                <div className="text-[10px] text-gray-500">Wake: 2:15 AM</div>
                <div className="text-[10px] text-gray-500">Sleep: 3:40 AM</div>
                <div className="text-[10px] text-gray-500">Notes: Crying</div>
              </div>
              
              <div className="absolute bottom-10 left-6 rotate-3 bg-yellow-50 p-3 rounded-lg shadow-lg max-w-[120px]">
                <div className="text-xs border-b border-yellow-200 pb-1 mb-1 font-medium">Email Note</div>
                <div className="text-[10px] text-gray-500">Baby woke twice...</div>
                <div className="text-[10px] text-gray-500">Late nap yesterday</div>
              </div>
              
              <div className="absolute bottom-24 right-10 rotate-6 bg-blue-50 p-2 rounded-lg shadow-lg max-w-[100px]">
                <div className="text-[10px] text-gray-500 grid grid-cols-3 gap-1">
                  <div className="border border-gray-200 p-1 text-center">Mon</div>
                  <div className="border border-gray-200 p-1 text-center">Tue</div>
                  <div className="border border-gray-200 p-1 text-center">Wed</div>
                  <div className="border border-gray-200 p-1 text-center">6h</div>
                  <div className="border border-gray-200 p-1 text-center">7h</div>
                  <div className="border border-gray-200 p-1 text-center">5h</div>
                </div>
              </div>
            </div>
            
            <div className="mt-10 space-y-4">
              {[
                {
                  icon: <Search className="h-5 w-5 text-red-500" />,
                  title: "Scattered logs make it hard to find patterns."
                },
                {
                  icon: <Clock className="h-5 w-5 text-red-500" />,
                  title: "Reviewing data manually takes 3-4 hours per client."
                },
                {
                  icon: <FileText className="h-5 w-5 text-red-500" />,
                  title: "Without automated insights, you rely on guesswork instead of trends."
                }
              ].map((painPoint, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 + (index * 0.1) }}
                  className="flex items-center space-x-3"
                >
                  <div className="flex-shrink-0 bg-red-100 p-2 rounded-full">
                    <X className="h-4 w-4 text-red-600" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-shrink-0">
                      {painPoint.icon}
                    </div>
                    <p className="text-gray-700">{painPoint.title}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* Right Column - After */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <div className="aspect-w-4 aspect-h-3">
                <img 
                  src="https://gbmarketplacestorage.blob.core.windows.net/sleepnest/modern.png" 
                  alt="Calm sleep consultant with digital dashboard" 
                  className="object-cover w-full h-full brightness-105"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-900/40 to-transparent"></div>
              </div>
              
              {/* Floating Dashboard Elements */}
              <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-800">Sleep Quality Improving</span>
                </div>
              </div>
              
              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg max-w-[180px]">
                <div className="text-xs font-medium text-gray-800 mb-1">AI Insight:</div>
                <p className="text-xs text-gray-600">Night wakings decreased by 60% after adjusting bedtime routine</p>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-md p-4 rounded-xl shadow-xl">
                <div className="text-sm font-medium text-gray-800 mb-2">Sleep Analysis Dashboard</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-indigo-50 p-2 rounded-lg">
                    <div className="text-xs font-medium text-indigo-800">Avg. Sleep</div>
                    <div className="text-lg font-bold text-indigo-600">9.2h</div>
                  </div>
                  <div className="bg-green-50 p-2 rounded-lg">
                    <div className="text-xs font-medium text-green-800">Wakings</div>
                    <div className="text-lg font-bold text-green-600">↓ 60%</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-10 space-y-4">
              {[
                {
                  icon: <Search className="h-5 w-5 text-indigo-500" />,
                  title: "Clear visualization of patterns at a glance."
                },
                {
                  icon: <Clock className="h-5 w-5 text-indigo-500" />,
                  title: "Cut analysis time by 75% with our AI-powered dashboard."
                },
                {
                  icon: <FileText className="h-5 w-5 text-indigo-500" />,
                  title: "Make confident recommendations based on data trends."
                }
              ].map((benefit, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                  className={cn(
                    "flex items-center space-x-3 p-3 rounded-xl transition-colors",
                    "bg-white border border-gray-100 shadow-sm"
                  )}
                >
                  <div className="flex-shrink-0 bg-indigo-100 p-2 rounded-full">
                    <div className="h-4 w-4 text-indigo-600">✓</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <p className="text-gray-700 font-medium">{benefit.title}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
